import Button from '@/components/Button';
import EnvironmentTag from '@/components/EnvironmentTag.jsx';
import ModalV2 from '@/components/ModalV2';
import sendToast from '@/lib/sendToast';
import { replayWorkflowAction } from '@/redux/actions/gravityActions';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors';
import { query } from '@/redux/selectors/querySelectors';
import selectors from '@/redux/selectors/selectors';
import { isLoading } from '@/redux/selectors/uiSelectors';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import workflowScreenSelectors from '../redux/selectors/workflowScreenSelectors';
import getLogTimeMachine from './lib/getLogTimeMachine';
import getReplayRangeModel, { findSpanContaining } from './lib/getReplayRangeModel';
import { exitReplayMode } from './ReplayModeBanner';

const ReplayFooterBar = () => {
  const [showConfirm, setShowConfirm] = useState(false);
  const workflowId = useSelector(selectors.workflowId);
  const workflowLogId = useArgSelector(query, 'logId');
  const activeLog = useArgSelector(selectors.workflowLogsById, workflowLogId);
  const output = useArgSelector(selectors.workflowLogOutputById, workflowLogId);
  const operationLogs = useArgSelector(selectors.workflowOperationLogsByLogId, workflowLogId);
  const time = useArgSelector(query, 'time');
  const replayStart = useArgSelector(query, 'replayStart');
  const replayEnd = useArgSelector(query, 'replayEnd');
  const isReplayLoading = useArgSelector(isLoading, 'replayWorkflowAction');
  const workflowSnapshotVersion = useSelector((state) => workflowScreenSelectors.activeWorkflowSnapshots(state)?.find((snapshot) => snapshot.id === activeLog?.snapshotId)?.version);
  const environments = useSelector(selectors.environments) || [];
  const environmentLabel = environments.find((e) => e.id === activeLog?.environmentId)?.name || 'Production';
  const versionLabel = workflowSnapshotVersion || 'Draft';

  // Must mirror the inputs LogSteps feeds getLogTimeMachine: the synthetic trigger
  // row shifts every execution index, so a mismatch here would shift the range.
  const triggerStep = activeLog?.meta?.triggerStep;
  const invokedBy = activeLog?.meta?.invokedBy;
  const manualUserId = invokedBy?.startsWith('manual-') ? invokedBy?.split('-')[1] : null;
  const { allSteps, timeMachine } = useMemo(() => {
    return getLogTimeMachine({ output, operationLogs, time, triggerStep, invokedBy, manualUserId });
  }, [output, operationLogs, time, triggerStep, invokedBy, manualUserId]);

  const model = useMemo(() => {
    return getReplayRangeModel({ allSteps, timeMachine, steps: output?.meta?.steps });
  }, [allSteps, timeMachine, output?.meta?.steps]);

  if (!model.spans.length) {
    return null;
  }

  const start = Number.isFinite(parseInt(replayStart, 10)) ? parseInt(replayStart, 10) : model.min;
  const end = Number.isFinite(parseInt(replayEnd, 10)) ? parseInt(replayEnd, 10) : model.max;
  const startSpan = findSpanContaining(model.spans, start);
  const endSpan = findSpanContaining(model.spans, end);
  const actionsCount = Math.max(end - start + 1, 1);

  const startSpanIndex = model.spans.indexOf(startSpan);
  const endSpanIndex = model.spans.indexOf(endSpan);
  const rangeStepNames = model.spans //
    .slice(startSpanIndex, endSpanIndex + 1)
    .map((span) => {
      const message = output?.meta?.steps?.find((n) => n.id === span.stepId)?.message;
      if (!message) {
        return null;
      }
      const iterations = timeMachine?.filter((entry) => entry.stepId === span.stepId)?.pop()?.children;
      return iterations > 0 ? `${message} (${iterations} iteration${iterations === 1 ? '' : 's'})` : message;
    })
    .filter(Boolean);

  const handleConfirm = () => {
    replayWorkflowAction({ workflowId, workflowLogId, startStepId: startSpan.stepId, endStepId: endSpan.stepId, startAction: start, endAction: end }, (res) => {
      setShowConfirm(false);
      if (res?.success === false) {
        sendToast({ message: res?.message || 'Error replaying actions', type: 'error', duration: 6000 });
        return;
      }
      sendToast({ message: 'Replay queued. The new run will appear in this list shortly.', type: 'success', duration: 4000 });
      exitReplayMode();
    });
  };

  const sourceRunLabel = activeLog?.startedOn ? format(new Date(activeLog.startedOn), 'MMM d, h:mm a') : 'this run';

  return (
    <>
      <div className="replay-footer-bar">
        <span className="sum">{`Replay Actions ${start} → ${end}`}</span>
        <span className="chips">
          <span className="chip">{`${actionsCount} action${actionsCount === 1 ? '' : 's'}`}</span>
          <EnvironmentTag environmentId={activeLog?.environmentId} overrideName={environmentLabel} />
          <span className="chip">{`Version ${versionLabel}`}</span>
          <span className="chip">Inputs from this run</span>
        </span>
        <span className="spacer" />
        <Button className="ghost" onClick={exitReplayMode}>
          Cancel
        </Button>
        <Button className="primary" loading={isReplayLoading} onClick={() => setShowConfirm(true)}>
          {`Replay ${actionsCount} Action${actionsCount === 1 ? '' : 's'}`}
        </Button>
      </div>
      <ModalV2 //
        show={showConfirm}
        portal
        centered
        width={470}
        title={`Replay Actions ${start} → ${end}?`}
        onHide={() => setShowConfirm(false)}
        actions={[
          { children: 'Cancel', onClick: () => setShowConfirm(false) },
          { children: 'Start Replay', className: 'primary', loading: isReplayLoading, onClick: handleConfirm }
        ]}>
        <div className="replay-confirm">
          <div className="row">
            <b>{`${actionsCount} action${actionsCount === 1 ? '' : 's'}`}</b>
            <span>{` will run again: ${rangeStepNames.join(', ')}.`}</span>
          </div>
          <div className="row">{`Actions before the range are not re-run — their outputs are loaded from this run's captured log (${sourceRunLabel}).`}</div>
          <div className="row">
            {'Runs '}
            <b>{`version ${versionLabel}`}</b>
            {' in '}
            <b>{environmentLabel}</b>
            {' with current credentials. Tasks are counted as a normal run.'}
          </div>
          <div className="warn">These actions execute against live systems — records created, updated, or emailed by them will happen for real.</div>
        </div>
      </ModalV2>
    </>
  );
};

export default ReplayFooterBar;
